import moment from "moment";
import * as XLSX from "xlsx";
import { getStorageItem, renderClaimStatus } from "./utils";
import { Utils } from "../constants/utils";
import { productClaimType, sectionTitle } from "../mapping";
import { getCurrentDate } from "./date";
import {
  DATE_OF_DAMAGE_SLUG,
  DAMAGED_ASSET_PARTS_LIST,
  DAMAGED_CYCLE_PARTS_SLUG,
  DAMAGE_DETAILS_SLUG,
  DEFAULT_CITY_NAME,
  DEFAULT_PINCODE,
  DEFAULT_STATE_NAME,
  DOCUMENT_CDN_PATH,
  DOWNLOAD_CLAIM_FILE_NAME,
  DOWNLOAD_CLAIM_SHEET_NAME,
  ERROR_MESSAGE_TEXT,
  FORMAT_DOB,
  FORMAT_YYYY_MM_DD,
  FORMAT_YYYY_MM_DD_HMS,
  GB_SLUG,
  GST_PERCENTAGE,
  LOCATION_OF_DAMAGE_SLUG,
  PASSWORD_SLUG,
  PAYMENT_FAILED_MESSAGE,
  SERVICE_CENTRE_ID,
  SERVICE_CENTRE_SLUG,
  SLUG_SKIP_COPAYMENT,
  SOME_THING_WENT_WRONG_TEXT,
  THIS_MONTH_TEXT,
  THIS_WEEK_TEXT,
  THIS_YEAR_TEXT,
  USER,
  USER_DETAILS_SLUG,
} from "../constants";
import {
  FIELDS_HAVING_ALPHABET_AND_SPACE_ONLY,
  FIXED_LENGTH_FIELDS,
  ONLY_ALPHA_NUMERIC_FIELDS,
  ONLY_ALPHA_NUMERIC_SPACE_FIELDS,
  ONLY_DIGIT_FIELDS,
} from "../constants/field";

export const buildClaimsList = (claims) => {
  claims.map((item) => {
    item.status_slug = item?.status.toLowerCase().replaceAll(" ", "_");
    item.status_text = renderClaimStatus(item.status);
    item.status_text_slug = item?.status_text.toLowerCase().replaceAll(" ", "_");
  });

  return claims;
};

export const buildGeneratePaymentLinkPayload = (entity_id) => {
  return {
    entity: "claim",
    entity_id,
    is_monthly: false,
    is_recurring: false,
    payment_method: false,
    payment_type: "CLAIM_REPAIR"
  }
};

export const buildInitiateClaimPayload = (product_id, service_centre_id) => {
  return {
    claim_type_id: [productClaimType[product_id]],
    service_centre_id,
  };
};

export const buildLoginPayload = ({ email, password }) => {
  return {
    email,
    password,
    grant_type: PASSWORD_SLUG,
    user_type: SERVICE_CENTRE_SLUG,
  };
};

export const buildRazorpayResponseErrorMessage = () => {
  return {
    data: {
      error_msg: PAYMENT_FAILED_MESSAGE,
    },
  };
};

export const buildResendOtpPayload = ({ email, mobile, name }) => {
  const payload = buildLoginPayload({ mobile });

  return { ...payload, email, name };
};

export const buildSubmitClaimInput = ({
  city = DEFAULT_CITY_NAME,
  state = DEFAULT_STATE_NAME,
  pincode = DEFAULT_PINCODE,
  damage_details = "",
}) => {
  return {
    inputs: [
      {
        name: DATE_OF_DAMAGE_SLUG,
        value: getCurrentDate().format(FORMAT_YYYY_MM_DD_HMS),
      },
      {
        name: LOCATION_OF_DAMAGE_SLUG,
        value: {
          city,
          state,
          pincode,
        },
      },
      {
        name: DAMAGE_DETAILS_SLUG,
        value: damage_details,
      },
      {
        name: DAMAGED_CYCLE_PARTS_SLUG,
        value: DAMAGED_ASSET_PARTS_LIST,
      },
    ],
  };
};

export const buildSubmitOtpPayload = ({ mobile, otp }) => {
  return {
    mobile,
    otp,
    type: USER,
  };
};

export const buildSystemError = () => {
  return {
    data: {
      error_msg: SOME_THING_WENT_WRONG_TEXT,
    },
  };
};

export const buildSystemErrorMessage = () => {
  return {
    data: {
      error_msg: ERROR_MESSAGE_TEXT,
    },
  };
};

export const convertToInteger = (value) => (value ? parseInt(value) : null);

export const downloadExcel = (data) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, DOWNLOAD_CLAIM_SHEET_NAME);
  XLSX.writeFile(workbook, DOWNLOAD_CLAIM_FILE_NAME);
};

export const getDurationLabel = (selectedTab) => {
  let label = THIS_WEEK_TEXT;

  if (selectedTab === 1) {
    label = THIS_MONTH_TEXT;
  } else if (selectedTab === 2) {
    label = THIS_YEAR_TEXT;
  }

  return label;
};

export const getSectionTitle = (str) =>
  sectionTitle[removeTrailingBackslash(removeDigits(str))];

export const getServiceCentreDetails = (keyname = SERVICE_CENTRE_ID) =>
  getStorageItem(USER_DETAILS_SLUG) &&
  JSON.parse(getStorageItem(USER_DETAILS_SLUG))[keyname];

export const isOnlyAlphaNumericField = (fieldName) =>
  ONLY_ALPHA_NUMERIC_FIELDS.indexOf(fieldName) > -1;

export const isOnlyAlphaNumericSpaceField = (fieldName) =>
  ONLY_ALPHA_NUMERIC_SPACE_FIELDS.indexOf(fieldName) > -1;

export const isOnlyDigitField = (fieldName) =>
  ONLY_DIGIT_FIELDS.indexOf(fieldName) > -1;

export const isFixedLengthField = (fieldName) =>
  FIXED_LENGTH_FIELDS.indexOf(fieldName) > -1;

export const isLoginNextButton = (
  { email, password, hasError },
  errorMessage
) =>
  !email ||
  (email && errorMessage.email) ||
  !password ||
  (password && errorMessage.password) ||
  hasError;

export const isCopaymentSkippedForClaim = (value) => value === SLUG_SKIP_COPAYMENT;

export const isValidDate = (dateString) =>
  moment(dateString, FORMAT_YYYY_MM_DD, true).isValid() === true;

export const reBuildOrderDetails = (orderDetails) => {
  const shallowCopy = { ...orderDetails };
  const totalPremium = Number(orderDetails?.premium);
  const gst = orderDetails.gst_percentange ?? GST_PERCENTAGE;
  const gstPercentage = Number(gst) / 100;
  const divisor = 1 + gstPercentage;
  const excludingGst = Number((totalPremium / divisor).toFixed(2));
  const gstAmount = Number((totalPremium - excludingGst).toFixed(2));

  return {
    ...shallowCopy,
    premium_total: totalPremium,
    premium_excluding_gst: excludingGst,
    premium_gst: gstAmount,
  };
};

export const removeDigits = (str) => str.replace(/[0-9]/g, "");

export const removeTrailingBackslash = (str) => str.replace(/\/$/, "");

export const validateInputLength = (event) => {
  const elName = event.target.name;
  let elValue = event.target.value;

  if (FIELDS_HAVING_ALPHABET_AND_SPACE_ONLY.indexOf(elName) > -1) {
    elValue = elValue.replace(/(\s{2,})|[^a-zA-Z']/g, " ");
    elValue = elValue.replace(/^\s*/, "");
    event.target.value = elValue;
  }

  if (isOnlyDigitField(elName)) {
    event.target.value = elValue.replace(/[^0-9]+/g, "");
  }

  if (isOnlyAlphaNumericField(elName)) {
    event.target.value = elValue.replace(/[^0-9A-Za-z]+/g, "").toUpperCase();
  }

  if (isOnlyAlphaNumericSpaceField(elName)) {
    event.target.value = elValue.replace(/[^0-9A-Za-z\s]+/g, "").toUpperCase();
  }

  if (elValue.length > Utils.limit.digits[elName]) {
    event.target.value = elValue.substring(0, Utils.limit.digits[elName]);
  }
};
export const syncProposalDetails = ({
  name = null,
  email = null,
  pincode = null,
  city = null,
  state = null,
  address_line_1 = null,
  response_phone_brand: phone_brand = null,
  response_phone_model: phone_model = null,
  response_phone_color: phone_color = null,
  response_phone_storage: phone_storage = null,
  response_phone_ram: phone_ram = null,
  response_screen_photo: screen_photo = null,
  response_imei_number: imei_number = null,
  response_manufactured_date: manufactured_date = null,
  response_serial_number: serial_number = null,
  product_name = null,
  response_coverage_period: coverage_period = null,
  start_date = null,
  end_date = null,
  response_invoice_amount: invoice_amount = null,
  response_invoice_number: invoice_number = null,
  response_invoice_date: invoice_date = null,
  response_invoice: invoice = null,
  policy_document = null,
  policy_number = null,
  toffee_id = null,
  estimate_amount = null,
} = {}) => {
  const splittedScreenPhoto = screen_photo ? screen_photo.split("/") : [];
  const screenLength = splittedScreenPhoto.length;
  const screen_photo_name = screenLength
    ? splittedScreenPhoto[screenLength - 1]
    : null;
  const screen_photo_link = screen_photo
    ? `${DOCUMENT_CDN_PATH}${screen_photo}`
    : null;

  const splittedInvoicePhoto = invoice ? invoice.split("/") : [];
  const invoiceLength = splittedInvoicePhoto.length;
  const invoice_name = invoiceLength
    ? splittedInvoicePhoto[invoiceLength - 1]
    : null;
  const invoice_link = invoice ? `${DOCUMENT_CDN_PATH}${invoice}` : null;

  const splittedPolicyDocument = policy_document
    ? policy_document.split("/")
    : [];
  const documentLength = splittedPolicyDocument.length;
  const policy_document_name = documentLength
    ? splittedPolicyDocument[documentLength - 1]
    : null;

  return {
    name,
    email,
    pincode,
    city,
    state,
    address: address_line_1,
    phone_brand,
    phone_model,
    phone_color,
    phone_storage: phone_storage ? phone_storage : null,
    phone_ram: phone_ram ? phone_ram : null,
    screen_photo,
    screen_photo_name,
    screen_photo_link,
    imei_number,
    manufactured_date,
    serial_number,
    product_name,
    coverage_period,
    start_date: start_date ? moment(start_date).format(FORMAT_DOB) : "",
    end_date: end_date ? moment(end_date).format(FORMAT_DOB) : "",
    invoice_amount,
    invoice_number,
    invoice_date,
    invoice,
    invoice_name,
    invoice_link,
    policy_document,
    policy_document_name,
    policy_number,
    toffee_id,
    estimate_amount,
    ex_showroom_price: invoice_amount,
  };
};

export const toSentenceCase = (string) =>
  string.charAt(0).toUpperCase() + string.slice(1);

export const toTitleCase = (str) => {
  return str
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (s) => s.toUpperCase());
};
